import { CountryCode } from '@dayetopia/types';
import { FormikValues } from 'formik';
import { unescape } from 'lodash';
import moment from 'moment';
import { GeneratedPPCData, generatePeriodPainConsultationData } from './periodPainConsultation';

/**
 * Formats a given date into a string with the format 'D MMMM YYYY'.
 * @param reportDate - The date to format.
 * @returns The formatted date string.
 */
export const formatReportDate = (reportDate: Date): string => {
  return moment(reportDate).format('D MMMM YYYY');
};

/**
 * Handles the migration of consultation data by creating an insight.
 * @param questionnaireRevisionId - The ID of the questionnaire revision.
 * @param googleIdToken - The Google ID token for authentication.
 * @param consultationData - The data of the consultation.
 * @param createInsight - Function to create an insight.
 */
export const handleMigration = async (
  questionnaireRevisionId: string,
  googleIdToken: string,
  consultationData: { [key: string]: string | boolean | number },
  createInsight: Function
) => {
  await createInsight(questionnaireRevisionId, googleIdToken, consultationData, true);
};

/**
 * Checks temporary storage for consultations and handles migration if necessary.
 * @param questionnaireRevisionId - The ID of the questionnaire revision.
 * @param googleIdToken - The Google ID token for authentication.
 * @param userInfo - User information object.
 * @param setPdfData - Function to set the PDF data.
 * @param getConsultations - Function to retrieve consultations.
 * @param createInsight - Function to create an insight.
 * @returns The generated PPC data or null if no completed consultation is found.
 */
export const checkTemporaryStorage = async (
  questionnaireRevisionId: string,
  googleIdToken: string,
  userInfo: any,
  getConsultations: (googleIdToken: string) => Promise<any>,
  createInsight: (questionnaireRevisionId: string, googleIdToken: string, insightData: FormikValues, completed: boolean) => any,
  country: CountryCode
): Promise<GeneratedPPCData | null> => {
  const consultations = await getConsultations(googleIdToken ?? '');

  if (consultations && consultations.length > 0) {
    if (userInfo.isMfaEnabled) {
      consultations
        .filter((consultation: { completed: boolean }) => consultation.completed)
        .forEach((consultation: { data: { [key: string]: string | boolean | number } }) => {
          handleMigration(questionnaireRevisionId, googleIdToken ?? '', consultation.data, createInsight);
        });
    }

    const consultation = consultations[0];
    if (!consultation.completed) {
      return null;
    }

    const json = JSON.parse(consultation.data);
    const date = new Date((consultation.completedDate ?? consultation.timestamp) * 1000);
    const data = generatePeriodPainConsultationData(json, country);
    return {
      ...data,
      timestampDate: date
    };
  }

  return null;
};

export const checkReportStorage = async (
  questionnaireRevisionId: string,
  googleIdToken: string,
  getCompletedInsight: (questionnaireRevisionId: string, googleIdToken: string) => any,
  country: CountryCode
): Promise<GeneratedPPCData | null> => {
  try {
    const insight = await getCompletedInsight(questionnaireRevisionId, googleIdToken);
    if (insight) {
      const json = JSON.parse(unescape(insight.raw));
      const { date } = insight;
      const data = generatePeriodPainConsultationData(json, country);
      return {
        ...data,
        timestampDate: date
      };
    }
  } catch (err) {
    console.debug(`Unable to find report in storage: ${err}`);
  }
  return null;
};

export function isCbdTamponsRecommended(painReportData: GeneratedPPCData | null, country: CountryCode) {
  if (!painReportData) {
    return false;
  }

  const prescriptions = painReportData?.prescriptions;

  const includeCbdTampons =
    prescriptions?.cbdTampons.eligible && prescriptions?.cbdTampons.recommended && country !== CountryCode.US && country !== CountryCode.IE;

  return includeCbdTampons;
}

export const isResourceRecommended = (resourceId: string, painReportData: GeneratedPPCData | null, country: CountryCode) => {
  if (!painReportData) {
    return false;
  }

  if (resourceId === 'cbd-tampons') {
    return isCbdTamponsRecommended(painReportData, country);
  }

  return true;
};
